import type { UserType } from '@form-factory/schema';
import { Role } from '@form-factory/schema/enums';
import { DateFormat, formatUtils } from '@form-factory/shared';
import { useLingui } from '@lingui/react/macro';
import { createColumnHelper } from '@tanstack/react-table';
import { Button, toast, Badge } from '@utima/ui';
import { Send } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { SimpleTooltip } from '@/components';
import { ActionsCell } from '@/components/dataTable/cells/ActionsCell';
import { useUser } from '@/hooks';

import { RemoveUserDialogButton } from './components/RemoveUserDialogButton';
import { RoleBadge } from './components/RoleBadge';
import { VerifiedBadge } from './components/VerifiedBadge';
import { useUserMutation } from './useUserMutation';
import { trpc } from '../../services/trpc';

const columnHelper = createColumnHelper<UserType>();

export function useUsersCols() {
  const { t } = useLingui();
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {},
  );

  const { currentOrganizationId, user } = useUser();
  const {
    remove: { mutateAsync: remove },
    removeFromOrganization: { mutateAsync: removeFromOrganization },
  } = useUserMutation();

  const { mutateAsync: sendVerify } = trpc.users.resendEmail.useMutation({
    onSettled: (_, __, variables) => {
      setLoadingStates(prev => ({ ...prev, [variables.id]: false }));
    },
    onSuccess: () => {
      toast.success(t`Sent`, {
        description: t`Activation email was successfully sent`,
      });
    },
    onError: () => {
      toast.error(t`Send failed`, {
        description: t`Failed to send activation email`,
      });
    },
  });

  const handleSendVerify = useCallback(
    (id: string) => {
      setLoadingStates(prev => ({ ...prev, [id]: true }));
      sendVerify({ id });
    },
    [sendVerify],
  );

  return useMemo(
    () => [
      columnHelper.accessor(row => row.id, {
        id: 'id',
        header: t`ID`,
        meta: {
          sortable: 'id',
        },
      }),
      columnHelper.accessor(
        row =>
          formatUtils.fullName({
            firstName: row.firstName || '',
            lastName: row.lastName || '',
          }),
        {
          id: 'name',
          header: t`Name`,
          meta: {
            sortable: 'name',
          },
        },
      ),
      columnHelper.accessor(row => row.email, {
        id: 'email',
        header: t`Email`,
        meta: {
          sortable: 'email',
        },
      }),
      columnHelper.accessor(row => row.createdAt, {
        id: 'createdAt',
        header: t`Created`,
        cell: row => formatUtils.date(row.getValue(), DateFormat.DateTime),
        meta: {
          sortable: 'createdAt',
        },
      }),
      columnHelper.accessor(row => row.role, {
        id: 'role',
        header: t`Role`,
        cell: ({ getValue, row }) => {
          return (
            <div className='flex flex-row flex-wrap gap-2 [&>*]:mb-2'>
              <RoleBadge role={getValue()} />
              {row.original.isActiveOperator && (
                <Badge color='primary' size='sm' outline>
                  {t`Active Operator`}
                </Badge>
              )}
            </div>
          );
        },
        meta: {
          sortable: 'role',
        },
      }),
      columnHelper.accessor(row => row.verified, {
        id: 'verified',
        header: t`Status`,
        cell: ({ getValue }) => <VerifiedBadge verified={getValue()} />,
        meta: {
          sortable: 'verified',
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: t`Actions`,
        cell: ({ row }) => (
          <ActionsCell
            onDelete={
              user?.id === row.original.id
                ? undefined
                : () => remove({ id: row.original.id })
            }
          >
            {!row.original.verified && (
              <SimpleTooltip title={t`Resend activation email`}>
                <Button
                  data-cta='admin-users-resend-activation-email-button'
                  onClick={() => handleSendVerify(row.original.id)}
                  loading={loadingStates[row.original.id]}
                  disabled={loadingStates[row.original.id]}
                  variant='ghost'
                  size='icon-xs'
                  outline
                  icon={<Send className='size-4' />}
                />
              </SimpleTooltip>
            )}
            {user?.id !== row.original.id &&
              (user?.role === Role.SuperAdmin || user?.role === Role.Admin) && (
                <RemoveUserDialogButton
                  userId={row.original.id}
                  organizationId={currentOrganizationId ?? ''}
                  onRemove={removeFromOrganization}
                  disabled={loadingStates[row.original.id]}
                />
              )}
          </ActionsCell>
        ),
        meta: {
          align: 'center',
        },
      }),
    ],
    [
      t,
      user?.id,
      user?.role,
      loadingStates,
      currentOrganizationId,
      removeFromOrganization,
      remove,
      handleSendVerify,
    ],
  );
}
